/**
 * The Techstars accelerator application as question data (LEV-2357).
 *
 * Mirrors https://www.techstars.com/application-preview: a Founder Profile, 18
 * Company Information items (item 8, "required materials", is the three
 * `attachment` questions), and an optional Program Selection step. The
 * generator writes toward each rubric and the evaluator scores against it, so
 * a change here changes both.
 *
 * Techstars ranks "team, team, team, market, progress, idea". The rubric
 * copy is mentor voice and names no Techstars staff.
 */
import { STEP_IDS } from "@/constants/steps";

import type { ApplicationQuestion } from "./types";

/** The official application, outside Lev — the submit checklist links here. */
export const TECHSTARS_APPLY_URL = "https://apply.techstars.com";

export const TECHSTARS_SELECTION_ORDER = [
  "team",
  "team",
  "team",
  "market",
  "progress",
  "idea",
] as const;

export const TECHSTARS_PART_LABELS: Record<ApplicationQuestion["part"], string> = {
  "founder-profile": "Founder Profile",
  company: "Company Information",
  attachment: "Required Materials",
  "program-selection": "Program Selection (optional)",
};

const CONTACT_FIELD_RUBRIC = {
  tenOutOfTen: ["Filled in, current, and the same on every founder's profile."],
  commonMistakes: ["Leaving it blank. A blank field reads as a founder who did not finish."],
  reviewersWeigh: "Completeness. Every finished field is a small signal that you go the extra mile.",
};

export const TECHSTARS_QUESTIONS: readonly ApplicationQuestion[] = [
  // --- Founder Profile ------------------------------------------------------
  {
    id: "founder-name",
    part: "founder-profile",
    label: "Founder name",
    sourceSteps: [STEP_IDS.TEAM],
    scoring: "completeness",
    detailKey: "founderName",
    missingNote: "Add your full name as it should appear on your {programName} application.",
    rubric: CONTACT_FIELD_RUBRIC,
  },
  {
    id: "founder-title",
    part: "founder-profile",
    label: "Title",
    sourceSteps: [STEP_IDS.TEAM],
    scoring: "completeness",
    detailKey: "founderTitle",
    missingNote: "Add your role at the company, such as CEO or CTO.",
    rubric: {
      tenOutOfTen: ["A plain role (CEO, CTO) that matches who does what in the team answer."],
      commonMistakes: ["Inflated or stacked titles (\"Founder, CEO & Visionary\") that blur who leads."],
      reviewersWeigh: "Whether roles are clear. Reviewers ask how you chose the CEO.",
    },
  },
  {
    id: "founder-email",
    part: "founder-profile",
    label: "Email",
    sourceSteps: [],
    scoring: "completeness",
    detailKey: "founderEmail",
    missingNote: "Add the email address {programName} should write to.",
    rubric: CONTACT_FIELD_RUBRIC,
  },
  {
    id: "founder-location",
    part: "founder-profile",
    label: "Location",
    sourceSteps: [],
    scoring: "completeness",
    detailKey: "founderLocation",
    missingNote: "Add the city you work from today.",
    rubric: {
      tenOutOfTen: ["Where you live and work today, consistent with the program you pick."],
      commonMistakes: ["A location that cannot reach the program's in-person sessions without saying how you will."],
      reviewersWeigh: "Program fit. Location decides the network you build and where you raise.",
    },
  },
  {
    id: "founder-linkedin",
    part: "founder-profile",
    label: "LinkedIn profile",
    sourceSteps: [STEP_IDS.TEAM],
    scoring: "completeness",
    detailKey: "founderLinkedin",
    missingNote: "Paste your LinkedIn profile URL.",
    rubric: {
      tenOutOfTen: ["A current profile whose history backs up the team answer."],
      commonMistakes: ["A stale profile that contradicts the roles or dates in the application."],
      reviewersWeigh: "Track record. Reviewers check that your background supports your claims.",
    },
  },
  {
    id: "founder-phone",
    part: "founder-profile",
    label: "Phone",
    sourceSteps: [],
    scoring: "completeness",
    detailKey: "founderPhone",
    missingNote: "Add the phone number {programName} can call you on.",
    rubric: CONTACT_FIELD_RUBRIC,
  },
  {
    id: "founder-full-time",
    part: "founder-profile",
    label: "Working on your startup full time?",
    sourceSteps: [STEP_IDS.TEAM],
    scoring: "scored",
    answerFormat: "yes-no",
    founderStory: ["if not yet full time, the date you go full time"],
    missingNote: "Say yes or no. If not yet, add the date you go full time.",
    rubric: {
      tenOutOfTen: [
        "A truthful yes or no.",
        "If not yet, the date you go full time and what triggers it.",
      ],
      commonMistakes: [
        "Saying yes while holding a job or a degree program. It surfaces in the interview.",
        "A bare \"no\" with no plan for the 90 days.",
      ],
      reviewersWeigh:
        "Commitment. The program expects senior management present for most of 90 days, and some programs require full exclusivity.",
    },
  },

  // --- Company Information, items 1-7 --------------------------------------
  {
    id: "startup-name",
    part: "company",
    label: "Startup name",
    sourceSteps: [],
    scoring: "completeness",
    detailKey: "companyName",
    missingNote: "Add your company's name as it appears on your site and deck.",
    rubric: {
      tenOutOfTen: ["The name on your product, site, deck and videos."],
      commonMistakes: ["A working name in one place and a new brand in another."],
      reviewersWeigh: "Consistency across every material you submit.",
    },
  },
  {
    id: "startup-url",
    part: "company",
    label: "Startup URL",
    sourceSteps: [],
    scoring: "completeness",
    detailKey: "companyUrl",
    missingNote: "Paste your company's website address. Reviewers click it.",
    rubric: {
      tenOutOfTen: ["A site that loads and says in one line what you do and for whom."],
      commonMistakes: ["A dead link, a parked domain, or a page of buzzwords."],
      reviewersWeigh: "Reviewers click it. It is part of your first impression.",
    },
  },
  {
    id: "year-founded",
    part: "company",
    label: "What year was your company founded?",
    sourceSteps: [],
    scoring: "completeness",
    detailKey: "yearFounded",
    missingNote: "Add the year you started working on the company, even if you have not incorporated yet.",
    rubric: {
      tenOutOfTen: ["The year you started working on it together, stated plainly."],
      commonMistakes: ["Leaving it blank because you have not incorporated yet."],
      reviewersWeigh: "Context for how much progress you made in how much time.",
    },
  },
  {
    id: "problem",
    part: "company",
    label: "What problem are you looking to solve?",
    sourceSteps: [STEP_IDS.CUSTOMER_PROBLEM],
    scoring: "scored",
    founderStory: ["how many customers you talked to and what they said"],
    rubric: {
      tenOutOfTen: [
        "Leads with the customer's pain, before any mention of the product.",
        "Names exactly who hurts (role, company type, situation), not a market category.",
        "Says what the pain costs them in time, money or risk, with a number.",
        "Says how they cope today: the spreadsheet, the email chain, the workaround.",
        "Shows you heard it from customers: how many you talked to and what they said.",
      ],
      commonMistakes: [
        "Leaving it blank or thin. Reviewers call a blank problem field a red flag.",
        "Describing the market (\"a $40B industry\") instead of a person's pain.",
        "Jumping to the solution in the first sentence.",
        "Trend jargon (\"disruptive AI-powered platform\") that hides the actual problem.",
      ],
      reviewersWeigh:
        "Whether you deeply understand your customer's pain. Plain words beat polish; the answer needs to be clear, not persuasive.",
    },
  },
  {
    id: "what-you-make",
    part: "company",
    label: "What are you going to make?",
    sourceSteps: [STEP_IDS.IDEA, STEP_IDS.PRODUCT_BUILD],
    scoring: "scored",
    rubric: {
      tenOutOfTen: [
        "One or two plain sentences on what it does and the value it creates for the customer named in the problem.",
        "Says what is built today and what is next.",
        "One clear immediate goal plus the big long-term goal, briefly.",
      ],
      commonMistakes: [
        "A feature list instead of the outcome for the customer.",
        "A long, rambling vision. Long answers are never effective.",
        "Stacked buzzwords (\"AI + IoT + blockchain\").",
      ],
      reviewersWeigh:
        "Clarity. The idea ranks last on Techstars' list because ideas change with data, so make it easy to understand and move on.",
    },
  },
  {
    id: "incorporation",
    part: "company",
    label: "Incorporation",
    sourceSteps: [],
    scoring: "completeness",
    detailKey: "incorporation",
    missingNote: "Add your entity type and where it is registered, or say you will form the company if accepted.",
    rubric: {
      tenOutOfTen: [
        "Entity type and jurisdiction (a U.S. C-corp or your home-country equivalent), or a plain \"not yet, will form on acceptance\".",
      ],
      commonMistakes: ["Guessing or leaving it blank. You can form the company after acceptance; just say so."],
      reviewersWeigh: "Readiness for the investment checks that come before funds are wired.",
    },
  },
  {
    id: "industries",
    part: "company",
    label: "Relevant industries",
    sourceSteps: [STEP_IDS.IDEA, STEP_IDS.TAM],
    scoring: "completeness",
    detailKey: "industries",
    missingNote: "Pick the two or three industries you actually sell into.",
    rubric: {
      tenOutOfTen: ["The two or three industries you actually sell into, matching the program you pick."],
      commonMistakes: ["Ticking every box to look broad. It reads as unfocused and hurts program matching."],
      reviewersWeigh: "Routing. Staff use industries to match you to the right program and Managing Director.",
    },
  },

  // --- Required materials (Company item 8) ---------------------------------
  {
    id: "pitch-deck",
    part: "attachment",
    label: "Pitch deck",
    sourceSteps: [
      STEP_IDS.CUSTOMER_PROBLEM,
      STEP_IDS.IDEA,
      STEP_IDS.TAM,
      STEP_IDS.COMPETITIVE,
      STEP_IDS.GTM,
      STEP_IDS.TEAM,
    ],
    scoring: "scored",
    missingNote: "Attach your pitch deck, about 10 slides.",
    rubric: {
      tenOutOfTen: [
        "About 10 slides that tell the same story as the written answers.",
        "A team slide early, not last, showing why these founders.",
        "Traction with real numbers and dates.",
      ],
      commonMistakes: [
        "A deck that contradicts the written answers.",
        "Design over substance, or a team slide buried at the end.",
      ],
      reviewersWeigh: "Consistency and clarity. The deck is read alongside every answer.",
    },
  },
  {
    id: "team-video",
    part: "attachment",
    label: "Team introduction video",
    limit: { unit: "seconds", max: 60, verified: false },
    sourceSteps: [STEP_IDS.TEAM],
    scoring: "scored",
    fedByFitStory: true,
    founderStory: [
      "how the founders met",
      "how long you have worked together",
    ],
    missingNote: "Record a one-minute video with every founder on camera.",
    rubric: {
      tenOutOfTen: [
        "Every founder on camera: who you are, how you met, how long you have worked together, why you.",
        "Personality and energy. Talk, do not read.",
        "About a minute. Rough is fine; a phone in a coffee shop works.",
        "Remote co-founders: each films half, or record a video call.",
      ],
      commonMistakes: [
        "Skipping it. Applications without a team video may be screened out.",
        "A scripted, over-produced or marketing-style video.",
        "Only one founder speaking.",
      ],
      reviewersWeigh: "The single most important material. It is the team, ranked first, seen directly.",
    },
  },
  {
    id: "demo-video",
    part: "attachment",
    label: "Product demo video",
    limit: { unit: "seconds", max: 60, verified: false },
    sourceSteps: [STEP_IDS.PRODUCT_BUILD, STEP_IDS.IDEA],
    scoring: "scored",
    missingNote: "Record a one-minute walkthrough of your product's core flow.",
    rubric: {
      tenOutOfTen: [
        "The real product working, in a straight walkthrough of the core flow.",
        "If pre-product, the closest real thing you have, saying plainly what is built and what is not.",
      ],
      commonMistakes: [
        "A marketing video or animated mockup. A rough demo with an ugly UI beats it every time.",
        "A long tour of every feature instead of the one that matters.",
      ],
      reviewersWeigh: "Progress and speed: proof you build, not just plan.",
    },
  },

  // --- Company Information, items 9-18 -------------------------------------
  {
    id: "winning-team",
    part: "company",
    label: "What makes your team a winning team?",
    sourceSteps: [STEP_IDS.TEAM],
    scoring: "scored",
    fedByFitStory: true,
    founderStory: [
      "how the founders met",
      "one thing you got wrong and changed your mind on",
      "something hard you pushed through",
    ],
    rubric: {
      tenOutOfTen: [
        "Why you are the best people in the world to solve this problem: lived experience, domain depth, or an unfair insight.",
        "How the founders met and how long you have worked together, with a concrete shared project.",
        "Complementary skills: who builds, who sells, and what each has shipped or sold before.",
        "Evidence of grit and speed: something hard you pushed through, or something you shipped in days that others take months on.",
        "Coachability: one thing you got wrong and changed your mind on.",
        "If solo, how you will attract the people you are missing, with names or roles in progress.",
        "Disclosure of any departed co-founder who still holds more than 5%.",
      ],
      commonMistakes: [
        "A list of titles and schools with no link to this problem.",
        "Skipping how the founders met. Reviewers always ask.",
        "Two founders with the same skills and no one who sells, or no one who builds.",
        "Hiding a co-founder departure or an awkward cap table detail.",
      ],
      reviewersWeigh:
        "Everything. Team is weighed three times over market, progress and idea, because a strong team can pivot. Aim for more than half of all the words in the application to be about the team.",
    },
  },
  {
    id: "traction",
    part: "company",
    label: "Share any traction",
    sourceSteps: [STEP_IDS.GTM, STEP_IDS.CUSTOMER_PROBLEM],
    scoring: "scored",
    founderStory: [
      "what customers taught you",
      "dates for the first build, the first user and the first payment",
    ],
    rubric: {
      tenOutOfTen: [
        "Absolute numbers plus a rate: \"42 paying pilots, 18% month over month for 3 months\".",
        "Pre-revenue proof counted precisely: customer interviews, letters of intent, waitlist, beta usage, a working prototype.",
        "What customers taught you, not just the count.",
        "How fast you got here: dates for the first build, the first user, the first payment.",
      ],
      commonMistakes: [
        "Adjectives instead of numbers (\"strong interest\", \"growing fast\").",
        "Overstating. Pre-revenue is fine; inflated numbers are not.",
        "Raw signups with no sign anyone uses or pays.",
      ],
      reviewersWeigh:
        "Progress, ranked just after team and market. There is no revenue minimum; any concrete proof of demand counts, and 20% monthly growth is the benchmark they cite.",
    },
  },
  {
    id: "revenue-funding",
    part: "company",
    label: "Share any revenue and funding you have",
    sourceSteps: [STEP_IDS.GTM],
    scoring: "scored",
    rubric: {
      tenOutOfTen: [
        "Exact revenue to date and current monthly revenue, or a plain \"pre-revenue\".",
        "Every dollar raised: amount, instrument (SAFE, note, equity), and from whom by type.",
        "Runway in months and a clean summary of the cap table.",
      ],
      commonMistakes: [
        "Rounding up, or mixing booked revenue with pipeline.",
        "Leaving out a past raise or a large holder. Dishonesty here is a stated rejection reason.",
      ],
      reviewersWeigh: "Transparency. Techstars says it values honesty and resilience over a big number.",
    },
  },
  {
    id: "business-model",
    part: "company",
    label: "How do you (or will you) make money? How much could you make?",
    sourceSteps: [STEP_IDS.GTM, STEP_IDS.TAM],
    scoring: "scored",
    rubric: {
      tenOutOfTen: [
        "Who pays, for what, and the price, in one sentence.",
        "A bottoms-up size: number of customers you can reach times what each pays.",
        "A credible path to a market that could support $1B or more in annual revenue.",
      ],
      commonMistakes: [
        "Top-down math (\"1% of a $50B market\").",
        "No price, or a price with no buyer named.",
        "A market too small to be venture scale, with no plan to expand.",
      ],
      reviewersWeigh: "Market, ranked right after team. Reviewers want venture scale and real pull.",
    },
  },
  {
    id: "target-audience",
    part: "company",
    label: "Who is your target audience?",
    sourceSteps: [STEP_IDS.CUSTOMER_PROBLEM, STEP_IDS.BRAND],
    scoring: "scored",
    rubric: {
      tenOutOfTen: [
        "The exact first customer: role, company type and size, situation.",
        "Who uses it and who pays, if they differ.",
        "Why this segment first, and who comes next.",
      ],
      commonMistakes: [
        "\"Small businesses\" or \"Gen Z\". Too broad to act on.",
        "Naming everyone who could ever use it.",
      ],
      reviewersWeigh: "Whether you know your customer well enough to find, sell to and retain them.",
    },
  },
  {
    id: "competitors",
    part: "company",
    label: "Who are your competitors or could become your competitors?",
    sourceSteps: [STEP_IDS.COMPETITIVE],
    scoring: "scored",
    rubric: {
      tenOutOfTen: [
        "Three to five named competitors: established players, other startups, and the do-it-yourself option (spreadsheet, email, an agency).",
        "Who could become a competitor: the incumbent who could add this as a feature.",
        "One line each on how customers use them today.",
      ],
      commonMistakes: [
        "Saying you have no competitors. If you truly find none, the market may be too early.",
        "Listing only weak or obscure competitors.",
        "Leaving out the status quo, which is usually the real competitor.",
      ],
      reviewersWeigh: "Competitive awareness. Vague or missing competitors is a published rejection reason.",
    },
  },
  {
    id: "competitive-advantage",
    part: "company",
    label: "What is your competitive advantage?",
    sourceSteps: [STEP_IDS.COMPETITIVE, STEP_IDS.BRAND],
    scoring: "scored",
    rubric: {
      tenOutOfTen: [
        "One specific reason you win against the competitors you just named.",
        "Why it is hard to copy: data, distribution, technology, or team insight.",
        "An advantage that is not price.",
      ],
      commonMistakes: [
        "\"Better UX\" or \"cheaper\" with nothing behind it.",
        "An advantage that disappears once a funded competitor notices.",
      ],
      reviewersWeigh: "Differentiation: what is meaningfully different and what is hard to copy.",
    },
  },
  {
    id: "why-now",
    part: "company",
    label: "Why is now the right time?",
    sourceSteps: [STEP_IDS.TAM, STEP_IDS.IDEA],
    scoring: "scored",
    rubric: {
      tenOutOfTen: [
        "The specific change (technology, regulation, cost, behavior) that makes this possible or urgent now.",
        "A date or a number that shows the change is real.",
        "Signs customers' patience with the current solution is wearing thin.",
      ],
      commonMistakes: [
        "\"AI is hot\" with no link to this customer.",
        "A reason that was just as true five years ago.",
      ],
      reviewersWeigh: "Timing is one of the three questions Techstars says every applicant must answer.",
    },
  },
  {
    id: "customer-acquisition",
    part: "company",
    label: "What is your customer acquisition strategy?",
    sourceSteps: [STEP_IDS.GTM],
    scoring: "scored",
    rubric: {
      tenOutOfTen: [
        "The channel that already brought your first customers, with numbers.",
        "What one customer costs to win and how long it takes.",
        "The next channel you will test and how you will know it works.",
      ],
      commonMistakes: [
        "A list of every channel (SEO, social, partnerships, ads) with no evidence.",
        "A plan on paper with nothing tried yet.",
      ],
      reviewersWeigh: "Scrappy, real customer acquisition. Investors back founders who already sell.",
    },
  },
  {
    id: "next-milestones",
    part: "company",
    label: "What are your next major milestones?",
    sourceSteps: [STEP_IDS.GTM, STEP_IDS.PRODUCT_BUILD],
    scoring: "scored",
    founderStory: ["target dates or time windows"],
    rubric: {
      tenOutOfTen: [
        "Two or three measurable milestones with dates for the next 3 to 6 months.",
        "The one metric you will move during the program.",
        "The long-term goal they ladder up to, in one line.",
      ],
      commonMistakes: [
        "Vague goals (\"grow the user base\", \"raise a round\").",
        "No dates, or milestones that are activities rather than results.",
      ],
      reviewersWeigh:
        "Execution speed. Reviewers will ask for your 3-month and 1-year roadmap and check whether you hit what you said.",
    },
  },

  // --- Program Selection ----------------------------------------------------
  {
    id: "program-choice",
    part: "program-selection",
    label: "Program selection",
    sourceSteps: [STEP_IDS.IDEA, STEP_IDS.TAM],
    scoring: "completeness",
    missingNote: "Pick your first and second choice {programName} programs, or leave it open and {programName} will route you.",
    rubric: {
      tenOutOfTen: [
        "A first and second choice picked on industry fit, location, and where you will raise.",
        "If left unassigned, answers written so they work for any program: Techstars staff route unassigned applicants.",
      ],
      commonMistakes: [
        "Picking by brand rather than fit.",
        "Applying to many programs. More applications do not raise your odds.",
      ],
      reviewersWeigh: "Fit beats brand. Your first choice is reviewed first, and staff may route you elsewhere.",
    },
  },
];
